import { useCallback, useState } from 'react'

import { produce } from 'immer'
import { useTranslation } from 'react-i18next'
import { type InfiniteData, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import {
  IconImageOutlineCounter,
  IconLockFill,
  IconVideoOutlineCounter
} from 'assets/svg/sprite'
import PriceModal from 'components/UI/Modal/Price/PriceModal'
import { useModalContext } from 'context/modalContext'
import { editPostPrice } from 'services/endpoints/post'
import { type ICreatorPriceLimits } from 'types/interfaces/IProfile'
import {
  type IInfinitePage,
  type IPostMediaCounter
} from 'types/interfaces/ITypes'

import styles from './postInfoBottom.module.scss'

interface Props {
  role: string
  price: number | null
  mediaCount: IPostMediaCounter
  hideCounter: boolean
  countOnly: boolean
  offset?: number
  premium?: boolean
  postId: string
  dataQuery?: readonly any[]
  isPlaying?: boolean
  tags?: any[]
  priceLimits?: ICreatorPriceLimits | null
  hasPreviewVideo?: boolean
  showPreviewVideo?: boolean
  onTogglePreviewVideo?: () => void
}

export const PostInfoBottom = ({
  role,
  price,
  mediaCount,
  hideCounter,
  countOnly,
  offset = 0,
  premium = false,
  postId,
  dataQuery,
  isPlaying,
  tags = [],
  priceLimits,
  hasPreviewVideo = false,
  showPreviewVideo = false,
  onTogglePreviewVideo
}: Props) => {
  const modalData = useModalContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showFeaturedText, setShowFeaturedText] = useState(false)

  const featuredUsers =
    tags
      ?.map(tag => tag.user)
      ?.filter(user => user?.avatar?.url && user?.display_name && user?.username) ?? []

  const hasPrice = role === 'owner' && !!price

  const updatePostInFeed = useCallback((newPrice: number) => {
    if (!dataQuery) return

    const cachedData =
      queryClient.getQueryData<InfiniteData<IInfinitePage>>(dataQuery)

    const newData = produce(cachedData, draft => {
      draft?.pages.forEach(page => {
        const postToUpdate = page.page.data.data.find(post => post.id === postId)
        if (postToUpdate) postToUpdate.price = newPrice
      })
    })

    queryClient.setQueryData(dataQuery, newData)
  }, [dataQuery, postId, queryClient])

  const { mutate: changePostPriceMutate } = useMutation(
    async (newPrice: number | string) =>
      await editPostPrice({ price: newPrice }, postId),
    {
      onSuccess: (_data: any, variables: number | string) => {
        if (dataQuery) updatePostInFeed(Number(variables))
      }
    }
  )

  const openPriceModal = () => {
    modalData.addModal(
      t('setPrice'),
      <PriceModal
        priceLimits={priceLimits}
        updatePrice={changePostPriceMutate}
      />
    )
  }

  const shouldShowCurrent = (current?: number | null) =>
    (current ?? 0) >= 1

  const showVideoBadge =
    typeof mediaCount.vids === 'number' &&
    (mediaCount.vids ?? 0) > 1

  const showImageBadge =
    typeof mediaCount.imgs === 'number' &&
    (mediaCount.imgs ?? 0) > 1

  return (
    <>
      {featuredUsers.length > 0 && (
        <div
          style={{
            bottom: `${offset}rem`,
            left: hasPrice ? '4.75rem' : '0'
          }}
          className={styles.featuredWrapper}
          onClick={() => setShowFeaturedText(prev => !prev)}
        >
          <div
            className={`
              ${styles.info}
              ${styles.featuredInfo}
              ${
                featuredUsers.length === 1 && !showFeaturedText
                  ? styles.singleFeaturedInfo
                  : ''
              }
            `}
          >
            <div className={styles.avatarStack}>
              {featuredUsers.map((user, index) => (
                <img
                  key={user.id ?? user.username}
                  src={user.avatar.url}
                  alt={user.display_name}
                  className={styles.avatar}
                  style={{ zIndex: index + 1 }}
                />
              ))}
            </div>

            {showFeaturedText && (
              <div className={styles.featuredUsers}>
                {featuredUsers.map((user, index) => (
                  <span key={user.id ?? user.username}>
                    <span
                      className={styles.featText}
                      onClick={event => {
                        event.stopPropagation()
                        navigate(`/${user.username}`)
                      }}
                    >
                      {user.display_name}
                    </span>

                    {index < featuredUsers.length - 1 && (
                      <span className={styles.featuredPipe} />
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {premium && (
        <div
          style={{
            bottom: `${
              offset +
              ((showImageBadge || showVideoBadge) ? 2.75 : 0)
            }rem`
          }}
          className={styles.wrapper}
        >
          {role !== 'owner' && (
            <div className={styles.info}>Premium</div>
          )}
        </div>
      )}

<div style={{ bottom: `${offset}rem` }} className={styles.wrapper}>
  {!isPlaying && (
    <>
      {role === 'owner' && !!price && (
        <div className={styles.pricePreviewGroup}>
          <div
            className={styles.info}
            style={{ cursor: 'pointer' }}
            onClick={openPriceModal}
          >
            <IconLockFill />
            <p>${price}</p>
          </div>

          {hasPreviewVideo && (
            <div
              className={`${styles.info} ${
                showPreviewVideo ? styles.previewActive : ''
              }`}
              style={{ cursor: 'pointer' }}
              onClick={event => {
                event.stopPropagation()
                onTogglePreviewVideo?.()
              }}
            >
              <p className={styles.previewText}>Preview</p>
            </div>
          )}
        </div>
      )}

      {!hideCounter && (
        <>
          {showVideoBadge && (
            <div className={styles.info}>
              <IconVideoOutlineCounter />
              <div className={styles.count}>
                <p>
                  {!countOnly && shouldShowCurrent(mediaCount.vid) && (
                    <span>{mediaCount.vid ?? 0} / </span>
                  )}
                  {mediaCount.vids}
                </p>
              </div>
            </div>
          )}

          {showImageBadge && (
            <div className={styles.info}>
              <IconImageOutlineCounter />
              <div className={styles.count}>
                <p>
                  {!countOnly && shouldShowCurrent(mediaCount.img) && (
                    <span>{mediaCount.img ?? 0} / </span>
                  )}
                  {mediaCount.imgs}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )}
</div>
    </>
  )
}